import { useEffect, useState } from 'react';
import { ReceiptsHistory } from '@/components/billing/ReceiptsHistory';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import NextActionBanner from '@/components/NextActionBanner';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { 
  Briefcase, FileText, Clock, Receipt, IndianRupee, ArrowRight, 
  Eye, Calendar, CheckCircle, AlertCircle, ChevronRight, Bell, CheckSquare,
  Download, PieChart, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
// iter-361: Featured Brand Partner placements on the Client Dashboard.
import SponsorBadge from '@/components/SponsorBadge';
// iter-367 — Client-facing AI Creator (sandboxed).
import ClientAICreatorPanel from '@/components/ClientAICreatorPanel';
// iter-386u-j — parity with Vendor / Admin: use the same 25/50/75/90/100
// bracket colours defined by ProgressSlider so progress bars look identical.
import { getProgressBgClass } from '@/components/ProgressSlider';

/**
 * CLIENT DASHBOARD
 * 
 * Similar styling to Vendor Dashboard but for Clients.
 * Shows:
 * - Projects assigned to this client
 * - Quotations published for their review (marked-up amounts)
 * - Invoices
 * - Payment status
 * - PENDING APPROVALS BANNER (NEW)
 * - TASKS NEARING DEADLINE (P0 NEW)
 * - PROJECT FUND LEDGER (P0 NEW - VIEW ONLY)
 * 
 * SECURITY: Only shows data where client_id matches current user
 */
export default function ClientDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myProjects, setMyProjects] = useState([]);
  const [myQuotations, setMyQuotations] = useState([]);
  const [myInvoices, setMyInvoices] = useState([]);
  const [projectProgress, setProjectProgress] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [nearingDeadlineTasks, setNearingDeadlineTasks] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fundLedgers, setFundLedgers] = useState([]); // NEW: Fund ledgers per project
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchClientData();
  }, []);

  const fetchClientData = async () => {
    try {
      const [projectsRes, quotationsRes, invoicesRes, progressRes, approvalsRes, deadlineRes] = await Promise.all([
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/client/quotations`).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices`).catch(() => ({ data: [] })),
        axios.get(`${API}/client/project-progress`).catch(() => ({ data: null })),
        axios.get(`${API}/client/pending-approvals`).catch(() => ({ data: [] })),
        axios.get(`${API}/tasks/nearing-deadline`).catch(() => ({ data: [] })),
      ]);

      setMyProjects(projectsRes.data);
      setMyQuotations(quotationsRes.data);
      setMyInvoices(invoicesRes.data);
      setProjectProgress(progressRes.data);
      setPendingApprovals(approvalsRes.data);
      setNearingDeadlineTasks(deadlineRes.data || []);
      
      // Fetch payment history and fund ledgers for all client projects
      if (projectsRes.data.length > 0) {
        const paymentPromises = projectsRes.data.map(p => 
          axios.get(`${API}/projects/${p.id}/payment-ledger`).catch(() => ({ data: null }))
        );
        const paymentResults = await Promise.all(paymentPromises);
        const allClientPayments = paymentResults
          .filter(r => r.data && r.data.client_payments)
          .flatMap(r => r.data.client_payments.map(p => ({ ...p, project_name: r.data.project_name })))
          .filter(p => p.status !== 'cancelled')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setPaymentHistory(allClientPayments.slice(0, 10));
        
        // Fetch fund ledgers (client view) for each project
        const fundLedgerPromises = projectsRes.data.map(p =>
          axios.get(`${API}/projects/${p.id}/fund-ledger/client-view`).catch(() => ({ data: null }))
        );
        const fundLedgerResults = await Promise.all(fundLedgerPromises);
        const validLedgers = fundLedgerResults
          .filter(r => r.data && r.data.project_id)
          .map(r => r.data);
        setFundLedgers(validLedgers);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Handle download receipt
  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };


  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading your dashboard...</div>
        </div>
      </Layout>
    );
  }

  // Calculate stats
  const activeProjects = myProjects.filter(p => p.status !== 'completed' && p.status !== 'cancelled');
  const pendingQuotations = myQuotations.filter(q => q.status === 'pending' || q.status === 'draft');
  const pendingInvoices = myInvoices.filter(inv => inv.status !== 'paid');
  const totalInvoiceValue = myInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const paidAmount = myInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-dashboard">
        <NextActionBanner />
        {/* Welcome Header - Same styling as Vendor */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Welcome back, {user.name}
          </h1>
          <p className="text-blue-100">
            {myProjects.length === 0 
              ? "Your projects will appear here once assigned."
              : `You have ${activeProjects.length} active project(s) and ${myQuotations.length} quotation(s) to review`
            }
          </p>
          {myQuotations.length > 0 && (
            <Link to="/client/quotations">
              <Button className="mt-4 bg-white text-blue-600 hover:bg-blue-50">
                <Eye size={16} className="mr-2" />
                View Quotations
              </Button>
            </Link>
          )}
        </div>

        {/* iter-361: Featured Brand Partner — top banner. AI cost / rate /
            recommendation prompts NEVER read this data. */}
        <div data-testid="client-dashboard-brand-top">
          <SponsorBadge zone="dashboard" limit={1} variant="rich-catalogue" />
        </div>

        {/* iter-367 — AI Creator (sandbox) — client-safe cost exploration. */}
        <ClientAICreatorPanel />

        {/* PENDING APPROVALS BANNER - Similar to AI Credits Request Banner */}
        {pendingApprovals.length > 0 && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-5 shadow-lg animate-pulse-slow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-full">
                  <Bell size={24} className="text-white" />
                </div>
                <div className="text-white">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <CheckSquare size={20} />
                    Action Required: {pendingApprovals.length} Task{pendingApprovals.length > 1 ? 's' : ''} Pending Your Approval
                  </h3>
                  <p className="text-amber-100 text-sm mt-1">
                    {pendingApprovals.slice(0, 2).map(t => t.title).join(', ')}
                    {pendingApprovals.length > 2 && ` and ${pendingApprovals.length - 2} more...`}
                  </p>
                </div>
              </div>
              <Link to="/client/approvals">
                <Button className="bg-white text-amber-600 hover:bg-amber-50 shadow-md">
                  Review Now
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* P0 FEATURE: Tasks Nearing Deadline (Next 7 Days) - CLIENT VIEW */}
        {nearingDeadlineTasks.length > 0 && (
          <div className="apple-card p-5 border-l-4 border-orange-500" data-testid="client-deadline-alerts">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <Clock size={20} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Tasks Nearing Deadline</h3>
                  <p className="text-sm text-stone-500">{nearingDeadlineTasks.length} task{nearingDeadlineTasks.length > 1 ? 's' : ''} due within 7 days</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {nearingDeadlineTasks.slice(0, 8).map((task) => (
                <div 
                  key={task.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    task.urgency === 'high' ? 'bg-red-50 hover:bg-red-100' :
                    task.urgency === 'medium' ? 'bg-orange-50 hover:bg-orange-100' :
                    'bg-amber-50 hover:bg-amber-100'
                  }`}
                  onClick={() => navigate(`/client/tasks`)}
                  data-testid={`client-deadline-task-${task.id}`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    task.urgency === 'high' ? 'bg-red-500 animate-pulse' :
                    task.urgency === 'medium' ? 'bg-orange-500' :
                    'bg-amber-400'
                  }`}></div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate text-sm">{task.title}</p>
                    <p className="text-xs text-stone-500 truncate">{task.project_name}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {task.requires_client_approval && (
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                        Needs Your Approval
                      </Badge>
                    )}
                    <span className={`text-xs font-medium ${
                      task.urgency === 'high' ? 'text-red-600' :
                      task.urgency === 'medium' ? 'text-orange-600' :
                      'text-amber-600'
                    }`}>
                      {task.days_until_deadline === 0 ? 'Due Today!' :
                       task.days_until_deadline === 1 ? 'Tomorrow' :
                       `${task.days_until_deadline}d left`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Link to="/projects" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Briefcase className="text-blue-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{activeProjects.length}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Active Projects</p>
          </Link>

          <Link to="/calendar" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group" data-testid="calendar-widget">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <Calendar className="text-red-600" size={20} />
              </div>
              <span className="text-2xl font-bold">📅</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Project Calendar</p>
          </Link>

          <Link to="/client/quotations" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="text-amber-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{myQuotations.length}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Quotations</p>
          </Link>

          <Link to="/client/invoices" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Receipt className="text-green-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{myInvoices.length}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Invoices</p>
          </Link>

          <Link to="/ai-budget-estimation" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <IndianRupee className="text-purple-600" size={20} />
              </div>
              <span className="text-2xl font-bold">₹{(totalInvoiceValue / 100000).toFixed(1)}L</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Total Value</p>
          </Link>
        </div>

        {/* iter-361: Featured Brand Partner — side card (sits next to stats
            on wide screens as a companion tile). */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 apple-card p-4 bg-gradient-to-br from-stone-50 to-white">
            <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
              <Bell size={14} className="text-stone-500" /> What&apos;s new for you
            </h3>
            <p className="text-xs text-stone-500">
              Track projects, review quotations and stay on top of approvals — plus discover trusted brand partners we work with.
            </p>
          </div>
          <div data-testid="client-dashboard-brand-sidebar">
            <SponsorBadge zone="dashboard" limit={1} variant="premium-minimal" />
          </div>
        </div>

        {/* No Projects Yet */}
        {myProjects.length === 0 && (
          <div className="apple-card p-8 text-center bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <Briefcase className="mx-auto mb-4 text-blue-500" size={56} />
            <h2 className="text-xl font-semibold text-stone-800 mb-2">
              No Projects Yet
            </h2>
            <p className="text-stone-600 mb-6 max-w-md mx-auto">
              Once you are assigned to a project, you will be able to view project details, 
              quotations, and invoices here.
            </p>
          </div>
        )}

        {/* Recent Quotations - PRIMARY SECTION */}
        <div className="apple-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText size={20} className="text-stone-600" />
              Your Quotations
            </h2>
            {myQuotations.length > 0 && (
              <Link to="/client/quotations">
                <Button variant="outline" size="sm">
                  View All
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            )}
          </div>
          
          {myQuotations.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <FileText className="mx-auto mb-3" size={40} />
              <p>No quotations available yet.</p>
              <p className="text-sm mt-1">Quotations will appear here once published for your review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myQuotations.slice(0, 5).map((quot) => (
                <div key={quot.id} className="p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{quot.quotation_number}</p>
                        <Badge 
                          variant="outline"
                          className={
                            quot.status === 'accepted' ? 'bg-green-100 text-green-700 border-green-200' :
                            quot.status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            quot.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                            'bg-stone-100'
                          }
                        >
                          {quot.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-500">
                        {quot.project_name} • {quot.items_count} items
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-800">
                        ₹{(quot.total || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-stone-500">
                        Valid until {formatDate(quot.valid_until)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Projects */}
        {myProjects.length > 0 && (
          <div className="apple-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Briefcase size={20} className="text-stone-600" />
              Your Projects
            </h2>
            
            <div className="space-y-3">
              {myProjects.slice(0, 5).map((project) => (
                <Link 
                  key={project.id} 
                  to={`/projects/${project.id}`}
                  className="block p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{project.name}</p>
                        <Badge 
                          variant="outline"
                          className={
                            project.status === 'in_progress' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                            project.status === 'completed' ? 'bg-green-100 text-green-700 border-green-200' :
                            project.status === 'on_hold' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-stone-100'
                          }
                        >
                          {project.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-500 flex items-center gap-2">
                        <Calendar size={14} />
                        {formatDate(project.start_date)} - {formatDate(project.end_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-800">
                        ₹{(project.budget || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-stone-500">Budget</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Project Progress - READ-ONLY (P0 FIX) */}
        {projectProgress && projectProgress.projects && projectProgress.projects.length > 0 && (
          <div className="apple-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle size={20} className="text-stone-600" />
                Project Progress
              </h2>
              <div className="text-sm text-stone-500">
                {projectProgress.summary.overall_progress}% Overall
              </div>
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{projectProgress.summary.total_tasks}</p>
                <p className="text-xs text-stone-500">Total Tasks</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-600">{projectProgress.summary.in_progress}</p>
                <p className="text-xs text-stone-500">In Progress</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{projectProgress.summary.completed}</p>
                <p className="text-xs text-stone-500">Completed</p>
              </div>
            </div>
            
            {/* Per-Project Progress */}
            <div className="space-y-4">
              {projectProgress.projects.map((proj) => (
                <div key={proj.project_id} className="p-4 bg-stone-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">{proj.project_name}</h3>
                    <span className="text-sm text-stone-500">{proj.overall_progress}% complete</span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-2 bg-stone-200 rounded-full overflow-hidden mb-3">
                    <div 
                      className={`h-full ${getProgressBgClass(proj.overall_progress || 0)} rounded-full transition-all`}
                      style={{ width: `${proj.overall_progress}%` }}
                    />
                  </div>
                  
                  {/* Task List (Read-Only) - Shows: Task Name | Status | Progress % */}
                  <div className="space-y-2">
                    {proj.tasks.slice(0, 5).map((task) => (
                      <div key={task.id} className="flex items-center justify-between py-1 text-sm border-b border-stone-100 last:border-0">
                        <span className="text-stone-700 truncate flex-1 pr-4">{task.title}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          task.status === 'completed' ? 'bg-green-100 text-green-700' :
                          task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          task.status === 'delayed' ? 'bg-red-100 text-red-700' :
                          'bg-stone-100 text-stone-600'
                        }`}>
                          {task.status.replace('_', ' ')}
                        </span>
                        <span className="w-16 text-right text-stone-500">{task.progress}%</span>
                      </div>
                    ))}
                    {proj.tasks.length > 5 && (
                      <p className="text-xs text-stone-400 text-center pt-2">
                        +{proj.tasks.length - 5} more tasks
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Invoices */}
        {pendingInvoices.length > 0 && (
          <div className="apple-card p-6 border-amber-200 bg-amber-50/50">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertCircle size={20} className="text-amber-600" />
              Pending Payments
            </h2>
            
            <div className="space-y-3">
              {pendingInvoices.slice(0, 3).map((invoice) => (
                <div key={invoice.id} className="p-4 bg-white rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{invoice.invoice_number}</p>
                      <p className="text-sm text-stone-500">
                        Due: {formatDate(invoice.due_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-amber-600">
                        ₹{(invoice.total || 0).toLocaleString()}
                      </p>
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                        {invoice.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <Link to="/invoices">
              <Button className="mt-4 w-full" variant="outline">
                View All Invoices
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {/* Payment Summary */}
        {myInvoices.length > 0 && (
          <div className="apple-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <IndianRupee size={20} className="text-stone-600" />
              Payment Summary
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-sm text-stone-500 mb-1">Total Invoiced</p>
                <p className="text-2xl font-bold text-stone-800">₹{totalInvoiceValue.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600 mb-1">Amount Paid</p>
                <p className="text-2xl font-bold text-green-700">₹{paidAmount.toLocaleString()}</p>
              </div>
            </div>
            
            {totalInvoiceValue > paidAmount && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 mb-1">Outstanding Balance</p>
                  <p className="text-xl font-bold text-amber-700">₹{(totalInvoiceValue - paidAmount).toLocaleString()}</p>
                </div>
                <CheckCircle className="text-amber-400" size={32} />
              </div>
            )}
          </div>
        )}

        {/* PROJECT FUND LEDGER - Client View (VIEW ONLY) */}
        {fundLedgers.length > 0 && (
          <div className="apple-card p-6" data-testid="client-fund-ledger">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Wallet size={20} className="text-blue-600" />
                Project Fund Ledger
              </h2>
              <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                <Eye size={12} className="mr-1" />
                View Only
              </Badge>
            </div>
            
            <div className="space-y-4">
              {fundLedgers.map((ledger) => (
                <div key={ledger.project_id} className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-stone-800">{ledger.project_name}</h3>
                    <Badge className={`${
                      ledger.payment_progress_percentage >= 100 ? 'bg-green-100 text-green-700 border-green-200' :
                      ledger.payment_progress_percentage >= 50 ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      'bg-amber-100 text-amber-700 border-amber-200'
                    }`}>
                      {ledger.payment_progress_percentage}% Paid
                    </Badge>
                  </div>
                  
                  {/* Contract Summary */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="p-3 bg-white rounded-lg text-center border">
                      <p className="text-xs text-stone-500 mb-1">Contract Value</p>
                      <p className="text-lg font-bold text-stone-800">₹{(ledger.contract_value || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center border border-green-100">
                      <p className="text-xs text-green-600 mb-1">Paid</p>
                      <p className="text-lg font-bold text-green-700">₹{(ledger.total_paid || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center border border-amber-100">
                      <p className="text-xs text-amber-600 mb-1">Balance Due</p>
                      <p className="text-lg font-bold text-amber-700">₹{(ledger.balance_due || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {/* Payment Stages Progress */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-stone-500 mb-2">Payment Stages</p>
                    {ledger.payment_stages?.map((stage, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-white rounded border border-stone-100">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            stage.paid ? 'bg-green-500' : 
                            stage.status === 'partial' ? 'bg-amber-500' : 'bg-stone-300'
                          }`} />
                          <span className="text-sm text-stone-700">{stage.label}</span>
                          <span className="text-xs text-stone-400">({stage.percentage}%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">₹{(stage.amount || 0).toLocaleString()}</span>
                          {stage.paid && (
                            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                              <CheckCircle size={10} className="mr-0.5" />
                              Paid
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Link to project detail */}
                  <Link 
                    to={`/projects/${ledger.project_id}`}
                    className="mt-3 block text-center text-sm text-blue-600 hover:underline"
                  >
                    View Project Details →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment History (Payment Ledger) with Receipt Download */}
        {paymentHistory.length > 0 && (
          <div className="apple-card p-6" data-testid="client-payment-history">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Receipt size={20} className="text-blue-600" />
                My Payments Made
              </h2>
              <Link to="/client/payment-history" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View All <ChevronRight size={14} />
              </Link>
            </div>
            
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-blue-700">₹{(payment.amount || 0).toLocaleString()}</p>
                      <p className="text-sm text-stone-500">{payment.project_name}</p>
                      <p className="text-xs text-stone-400 mt-1">{new Date(payment.created_at).toLocaleString('en-IN')}</p>
                      {payment.notes && (
                        <p className="text-sm text-stone-600 mt-2 italic">"{payment.notes}"</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                        <CheckCircle size={12} className="mr-1" />
                        Paid
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/payments/${payment.id}/receipt-pdf`, {
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `receipt_${payment.id}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success('Receipt downloaded');
                          } catch (error) {
                            toast.error('Failed to download receipt');
                          }
                        }}
                      >
                        <Receipt size={12} className="mr-1" />
                        Receipt
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* iter-361: Featured Brand Partner — bottom banner. */}
        <div className="pt-2" data-testid="client-dashboard-brand-bottom">
          <SponsorBadge zone="dashboard" limit={1} variant="premium-minimal" />
        </div>
        <ReceiptsHistory title="Payment receipts" />
      </div>
    </Layout>
  );
}
